import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import _get from 'lodash/get'
import { List, Map, fromJS } from 'immutable'
import { useQuery, useMutation } from '@apollo/client'
import FormsyForm from 'formsy-react'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import Sherpa, { TYPE_SMALL_BLUE } from 'components/Sherpa'
import { H5 } from 'components/Heading'
import { TextInput, Checkbox } from 'components/FormInput.v2'
import FeedbackBox, { FEEDBACK_BOX_TYPES } from 'components/FeedbackBox'
import { GET_PLAYLISTS, CREATE_PLAYLIST } from 'services/playlist/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from 'services/apollo-graphql'
import { formatPlaylistInputErrors, getPlaylistNameByType } from 'services/playlist'
import Button, { TYPES } from 'components/Button'
import { PLAY_LIST_CREATED_GA4_EVENT } from 'services/event-tracking'

const MultiplePlaylistsAddModalGraphql = (props) => {
  const {
    setDefaultGa4Event,
    clearPlaylistItem,
    getPlaylistItem,
    dismissModal,
    staticText,
    createOnly,
    contentId,
    onChange,
    uniqueId,
    authJwt,
    meta,
    uiLocation,
    uiElement,
    addToasty,
  } = props

  const [validForm, setValidForm] = useState(false)
  const [inputVisible, setInputVisible] = useState(false)

  const titleTextKey = createOnly ? 'titleCreate' : 'titleAdd'
  const metaPlaylists = meta.get('playlists', List())

  const { data } = useQuery(GET_PLAYLISTS, {
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: authJwt,
    },
  })

  const playlistsList = _get(data, ['playlistList', 'playlists'], []) || []
  const playlists = fromJS(playlistsList) || List()

  const [
    createPlaylistMutation,
    { loading: creatingPlaylist, error: createPlaylistError },
  ] = useMutation(CREATE_PLAYLIST, {
    context: {
      jwt: authJwt,
    },
  })

  const createPlaylist = (playlistName) => {
    // TODO add update cache logic once graphql lists implemented
    return createPlaylistMutation({
      variables: {
        playlistName,
      },
    })
  }

  const handleChange = useCallback((checked, playlist) => {
    onChange({
      playlistId: playlist.get('id'),
      playlistType: playlist.get('playlistType'),
      playlistName: getPlaylistNameByType(playlist, staticText),
      action: checked ? 'add' : 'remove',
    })
    dismissModal()
  }, [contentId, playlists])

  const onSubmit = async ({ playlistName }) => {
    const response = await createPlaylist(playlistName)

    if (!createPlaylistError) {
      const playlistData = _get(response, ['data', 'playlistCreate', 'playlist'], {}) || {}
      const playlist = fromJS(playlistData) || Map()

      // GA4 event
      setDefaultGa4Event(PLAY_LIST_CREATED_GA4_EVENT.mergeDeep({
        playlist_name: getPlaylistNameByType(playlist, staticText),
        playlist_type: 'Custom',
        ui_element: uiElement,
        ui_location: uiLocation,
      }))

      addToasty(staticText.get('successMessage'))

      if (!createOnly) {
        handleChange(true, playlist)
      } else {
        dismissModal()
      }
    }
  }

  const submitButton = useMemo(() => {
    if (!inputVisible && !createOnly) {
      return null
    }

    if (creatingPlaylist) {
      return <Sherpa type={TYPE_SMALL_BLUE} className={['playlist-empty__sherpa']} />
    }

    return (
      <Button
        type={TYPES.PRIMARY}
        submit
        disabled={!validForm}
        stacked
        className="multiple-playlists-add-modal__button"
      >
        {staticText.get('buttonSave')}
      </Button>
    )
  }, [
    inputVisible,
    createOnly,
    creatingPlaylist,
    validForm,
    staticText,
  ])

  const checkboxes = useMemo(() => {
    if (createOnly) {
      return null
    }

    return playlists.map((playlist) => {
      const type = playlist.get('playlistType')
      const id = playlist.get('id')
      const name = getPlaylistNameByType(playlist, staticText)
      const inPlaylist = !!metaPlaylists.find((metaPl) => metaPl.get('playlistType') === type)

      return (
        <Checkbox
          key={id}
          label={name}
          onChange={(_, checked) => handleChange(checked, playlist)}
          value={inPlaylist}
          name={id}
          block
        />
      )
    })
  }, [
    createOnly,
    playlists,
    metaPlaylists,
    staticText,
    handleChange,
    getPlaylistNameByType,
  ])

  useEffect(() => {
    // TODO getPlaylistItem uses REST endpoint, should be switched to graphql
    if (contentId) {
      getPlaylistItem({
        componentId: uniqueId,
        contentId,
      })
    }
    return () => {
      clearPlaylistItem({
        componentId: uniqueId,
        contentId,
      })
    }
  }, [uniqueId, contentId])

  return (
    <div className="multiple-playlists-add-modal">
      <FormsyForm
        className="multiple-playlists-add-modal"
        onValid={() => setValidForm(true)}
        onInvalid={() => setValidForm(false)}
        onValidSubmit={onSubmit}
      >
        <H5>{staticText.get(titleTextKey)}</H5>
        {!createOnly
          ? <div className="multiple-playlists-add-modal__list">
            {playlists.size && meta.size
              ? checkboxes
              : <Sherpa type={TYPE_SMALL_BLUE} className={['multiple-playlists-add-modal__sherpa']} />
            }
            </div>
          : null
        }
        {createPlaylistError && <FeedbackBox type={FEEDBACK_BOX_TYPES.ERROR}>
            {staticText.get('otherNameMessage')}
          </FeedbackBox>
        }
        {(inputVisible || createOnly) && <TextInput
            name="playlistName"
            label={staticText.get('placeholder')}
            validations={{
              maxLength: 40,
            }}
            validationErrors={{
              maxLength: staticText.get('inputMaxLengthMessage'),
            }}
            validationErrorFormat={formatPlaylistInputErrors}
            maxLength={100}
            autocomplete="off"
            value=""
            required
          />
        }
        {(!inputVisible && !createOnly) && <Button
            type={TYPES.SECONDARY}
            onClick={() => setInputVisible(true)}
            stacked
            className="multiple-playlists-add-modal__button"
          >
            {staticText.get('buttonCreate')}
          </Button>
        }
        {submitButton}
      </FormsyForm>
    </div>
  )
}

MultiplePlaylistsAddModalGraphql.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  authJwt: PropTypes.string.isRequired,
  meta: ImmutablePropTypes.map,
  createOnly: PropTypes.bool,
  uniqueId: PropTypes.string,
  contentId: PropTypes.number,
  onChange: PropTypes.func,
}

MultiplePlaylistsAddModalGraphql.defaultProps = {
  createOnly: false,
}

export default connectRedux(
  (state, { contentId }) => {
    return {
      staticText: state.staticText.getIn(['data', 'multiplePlaylistsAddModal', 'data'], Map()),
      meta: state.playlist.getIn(['items', contentId, 'data'], Map()),
      authJwt: state.auth.get('jwt'),
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      addToasty: actions.toasty.addToasty,
      clearPlaylistItem: actions.playlist.clearPlaylistItem,
      getPlaylistItem: actions.playlist.getPlaylistItem,
      dismissModal: actions.dialog.dismissModal,
      setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
    }
  },
)(MultiplePlaylistsAddModalGraphql)
